import { STORE_SERVICES } from '@/types/store/services.ts'
import helpers from '@/utils/helpers.ts'
import { useBetaFeatures } from '@/composables/beta-features'
import { useBox } from '@/composables/box/box'
import { useBoxInfo } from '@/composables/box/box-info'
import { useOrganizationBetaFeatures } from '@/composables/organization/organization-beta-features'
import type { UserMeOrganization } from '@deepcloud/api-deep-admin'
import type { Box, BoxInfo } from '@deepcloud/api-deep-box'
import type { MenuItem } from '@deepcloud/deep-ui-lib'

export function useDeepboxIntegrations(
  org: MaybeRefOrGetter<UserMeOrganization>,
  box?: MaybeRefOrGetter<Box | BoxInfo | undefined>,
) {
  const { t } = useI18n()

  const { isAbaNinjaBoxEnabled } = useOrganizationBetaFeatures()
  const { canDeepMail } = useBetaFeatures(computed(() => toValue(org)))

  const parentBoxId = computed(() => toValue(box)?.boxNodeId)

  const { hasBoxRelationByBoxType } = useBoxInfo(
    computed(() => toValue(box) as BoxInfo),
  )

  const { isBoxVariantAdvanced, isBoxVariantAdvancedPerUse } = useBox(
    computed(() => toValue(box) as BoxInfo),
  )

  const services = computed<MenuItem[]>(() => [
    {
      key: STORE_SERVICES.HOOP,
      title: 'Hoop',
      subtitle: t('external_services.hoop.description'),
      prepend: {
        deepAppIcon: {
          size: 32,
          logo: 'hoop',
        },
      },
      to: {
        name: 'organization-store-services-service',
        params: {
          organization: toValue(org)?.group_id,
          service: STORE_SERVICES.HOOP,
        },
        query: {
          parentBoxId: parentBoxId.value,
        },
      },
      show: () => !helpers.isPersonalStructure(toValue(org)),
    },
    {
      key: STORE_SERVICES.ABA_NINJA,
      title: 'DeepNinja',
      subtitle: t('external_services.aba_ninja.description'),
      prepend: {
        deepAppIcon: {
          size: 32,
          logo: 'deepninja',
        },
      },
      to: {
        name: 'organization-store-services-service',
        params: {
          organization: toValue(org)?.group_id,
          service: STORE_SERVICES.ABA_NINJA,
        },
        query: {
          parentBoxId: parentBoxId.value,
        },
      },
      show: () => {
        const isOrgValid =
          !!toValue(org) &&
          !!toValue(org).group_id &&
          isAbaNinjaBoxEnabled(toValue(org).group_id)
        if (toValue(box)) {
          return (
            isOrgValid &&
            !!toValue(box) &&
            (isBoxVariantAdvanced.value || isBoxVariantAdvancedPerUse.value) &&
            !hasBoxRelationByBoxType('deep-ninja')
          )
        }
        return isOrgValid
      },
    },
  ])

  const sortedServices = computed(() =>
    services.value.filter((i) => (i.show ? i.show() : true)),
  )

  const showDeepMailDialog = ref(false)

  const tools = computed<MenuItem[]>(() => [
    {
      key: 'deepmail',
      title: 'DeepMail',
      subtitle: t('external_services.deepmail.description'),
      prepend: {
        deepAppIcon: {
          size: 32,
          logo: 'deepmail',
        },
      },
      append: {
        flag: 'DEV',
      },
      show: () => canDeepMail.value,
      action: () => {
        showDeepMailDialog.value = true
      },
    },
  ])

  const sortedTools = computed(() =>
    tools.value.filter((i) => (i.show ? i.show() : true)),
  )

  return {
    sortedServices,
    sortedTools,
    showDeepMailDialog,
  }
}
